import {DropzoneHandler, Endpoints, FetchManager} from 'js-pimcore-formbuilder';

/**
 * Enables ajax forms for Formbuilder
 */
class FormbuilderAjax {
    static errorMsgClass = "error-message-ajax";
    static errorAddClass = "invalid-feedback d-block";
    static errorFieldClass = "error-field-ajax";
    static errorFormClass = "error-form-ajax";

    constructor() {
        throw new Error('FormbuilderAjax is a static class and cannot be initialized');
    }

    /**
     * Setup method of this class.
     * When html is parsed and loaded it search for all formbuilder form with ajax data upload
     * and retrieves endpoints (?) from BE.
     * Then on each found form it sets FetchManager and DropzoneHandler (DH is for sending files).
     * FetchManager operates on method we set up in him so look at:
     * - onSuccess - when form was succesfully sent
     * - onError - when there was error at BE
     * - addValidationMessage - creates validation messages on error
     * - removeFormValidations - removes validation messages
     */
    static init() {
        document.addEventListener('DOMContentLoaded', () => {
            // Retrieve forms
            let formBuilderForms = document.querySelectorAll('form.formbuilder.ajax-form');
            if (formBuilderForms.length == 0) {
                return;
            }

            // Retrieve endpoints (?) for all found forms at once
            new Endpoints().getEndpoints(formBuilderForms[0].dataset.ajaxStructureUrl)
                .then((endpoints) => {
                    formBuilderForms.forEach((formBuilderForm, i) => {
                        // Find alert container
                        const alert = formBuilderForm.querySelector('.col-12');
                        const handler = new FormbuilderAjaxHandler(formBuilderForm, alert);
                        new DropzoneHandler(formBuilderForm, {}, endpoints);
                        const fetch = new FetchManager(formBuilderForm, {
                            onSuccess: handler.onSuccess.bind(handler),
                            onError: handler.onError.bind(handler, 'error'),
                            onFail: handler.onError.bind(handler, 'fail'),
                            onFatalError: handler.onError.bind(handler, 'fatalerror'),
                            elementTransformer: {
                                addValidationMessage: handler.addValidationMessage.bind(handler),
                                removeFormValidations: handler.removeFormValidations.bind(handler),
                            },
                        }, endpoints);

                        /**
                         * BUG WORK-AROUNG
                         * Due to js-pimcore-formbuilder bug which assigns options into the default values of the FetchManager
                         * we have to reassign options to remove all references to default object so they don't get replaced
                         * in case more then one form is present on the page
                         */
                        fetch.options = Object.assign({}, fetch.options);
                    });
                }).catch((error) => {
                console.error('formbuilder error: fetching file structure', error);
            });
        });
    }

    /**
     * Removes old alerts from alert container
     */
    static removeOldAlert(alertContainer) {
        alertContainer.outerHtml = '';
    }

    /**
     * Creates alert for user. Possible alert types:
     * - success - default
     * - error
     * - warning
     * @param  {String} message          Message to display to user
     * @param  {String} [type='success'] Type of message
     */
    static alert(message, alertContainer, type = 'success') {
        const types = {
            success: 'alert-success',
            error: 'alert-danger',
            warning: 'alert-warning',
        }

        FormbuilderAjax.removeOldAlert(alertContainer);
        let alert = document.createElement('div');
        alert.className = 'alert ' + (types[type] || 'not-recognized-type');
        alert.innerText = message;
        alertContainer.appendChild(alert);
    }

    static transformToInputName(name, form) {
        const formName = form.getAttribute('name');
        const fieldName = name.substr(formName.length + 1);
        return formName + '[' + fieldName + ']';
    }
}

/**
 * Class for easier managing different ajax setups
 */
class FormbuilderAjaxHandler {
    /**
     * Form node
     * @type {Node}
     */
    form;

    /**
     * Node container for putting general messages
     * @type {Node}
     */
    alertContainer;

    constructor(form, alertContainer) {
        this.form = form;
        this.alertContainer = alertContainer;
    }

    /**
     * Shows alert when FetchManager returns success
     * @param  {Object} messages FetchManager success object with messages
     * @param  {?mixin} redirect No idea
     */
    onSuccess(messages, redirect) {
        //FormbuilderAjax.alert(messages[0].message, this.alertContainer);
        if (redirect) {
            setTimeout(function () {
                document.location.href = redirect;
            }, 2000);

        } else {
            //alert(message[0]['message']);
            FormbuilderAjax.alert(messages[0].message, this.alertContainer);
        }
    }

    /**
     * Shows alert when FetchManager returns error
     * @param  {String} type     Type of the error
     * @param  {Object} messages FetchManager error object with messages
     */
    onError(type, messages) {
        if (!messages.validation_errors) {
            return;
        }

        if (messages.validation_errors.general) {
            const error = messages.validation_errors.general.join(', ');
            FormbuilderAjax.alert(error, this.alertContainer, 'error');
            delete messages.validation_errors.general;
        }

        this.removeFormValidations(this.form)
        Object.keys(messages.validation_errors).forEach(formFieldName => {
            const fieldName = FormbuilderAjax.transformToInputName(formFieldName, this.form);
            const field = this.form.querySelector("[name='" + fieldName + "']");
            if (field) {
                this.addValidationMessage(this.form, field, messages.validation_errors[formFieldName]);
            }
        });

    }

    /**
     * Remove all old validation errors.
     * @param  {Node} form Form from which errors should be removed
     */
    removeFormValidations(form) {
        this.form.classList.remove(FormbuilderAjax.errorFormClass);
        this.form.querySelectorAll('.' + FormbuilderAjax.errorFieldClass).forEach((el) => el.classList.remove(FormbuilderAjax.errorFieldClass))
        this.form.querySelectorAll('.' + FormbuilderAjax.errorMsgClass).forEach((el) => el.remove());
    }

    getRootParent(form, possibleParent) {
        const parent = possibleParent.parentElement;
        if (parent == document.body) {
            throw new Error('Parent container was not found');
        }

        if (form.name == parent.id || form == parent) {
            return possibleParent;
        }

        return this.getRootParent(form, parent);
    }

    /**
     * Generates validation message.
     * Creates new nodes with error messages adds them to the failed input.
     * @param {Node}  form     Form containg failed input
     * @param {Node}  field    Failed input
     * @param {Array} messages Array of failed messages
     */
    addValidationMessage(form, field, messages) {
        if (!Array.isArray(messages)) {
            return;
        }
        this.form.classList.add(FormbuilderAjax.errorFormClass);
        field.classList.add(FormbuilderAjax.errorFieldClass);
        const parent = this.getRootParent(form, field.parentElement);
        const errors = parent.querySelectorAll('.' + FormbuilderAjax.errorMsgClass);

        // Making quick map of existing errors inside the field
        const errorsTxt = {};
        errors.forEach(errorNode => {
            errorsTxt[errorNode.innerText] = true;
        });

        messages.forEach(function (message) {
            // Check if the error isn't already there
            if (errorsTxt[message]) {
                return
            }

            let errorEl = document.createElement('div');
            errorEl.className = FormbuilderAjax.errorMsgClass + ' ' + FormbuilderAjax.errorAddClass;
            errorEl.innerText = message;
            parent.appendChild(errorEl);
        }.bind(FormbuilderAjax));
    }
}

FormbuilderAjax.init();
